import { useCallback, useEffect, useState } from "react";
import { Section, Field, StatusPill } from "@/components/Shell";
import { LogoFrame, ClientPlaceholderMark } from "@/components/Logos";
import { ADMIN } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Plus, Loader2, Power, PowerOff, Building2, Upload, Trash2, Pencil, X } from "lucide-react";

const FRONTEND_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "";
const MAX_LOGO_BYTES = 300 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function pickLogo(e) {
  const file = e.target.files?.[0];
  if (!file) return null;
  if (file.size > MAX_LOGO_BYTES) {
    toast.error("Logo must be under 300KB");
    return null;
  }
  return fileToDataUrl(file);
}

export default function AdminIntakesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    slug: "",
    description: "",
    logo_data_url: null,
  });
  const [editing, setEditing] = useState(null); // {id, ...}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/intakes");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    if (!form.client_name.trim()) return toast.error("Client name is required");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/intakes", {
        client_name: form.client_name.trim(),
        slug: form.slug.trim() || null,
        description: form.description.trim() || null,
        logo_data_url: form.logo_data_url || null,
        active: true,
      });
      toast.success(`Intake created for ${data.client_name}`);
      setForm({ client_name: "", slug: "", description: "", logo_data_url: null });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/admin/intakes/${it.id}`, {
        client_name: it.client_name,
        description: it.description,
        active: !it.active,
      });
      toast.success(`Intake ${!it.active ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/intakes/${editing.id}`, {
        client_name: editing.client_name,
        description: editing.description,
        logo_data_url: editing.logo_data_url ?? "",
        active: editing.active,
      });
      toast.success("Intake updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyLink = async (slug) => {
    const url = `${FRONTEND_ORIGIN}/apply/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="space-y-10">
      <Section kicker="Client campaigns" title="Intakes">
        {/* Create form */}
        <div className="brut-card p-5 mb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] mb-3">
            <Building2 size={12} className="inline -mt-0.5 mr-1" />
            Create a new client intake
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <Field label="Client / business name" required>
              <input
                className="input-brut"
                data-testid={ADMIN.intakeClientName}
                placeholder="e.g. KAO ENTERPRISE LLC"
                value={form.client_name}
                onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              />
            </Field>
            <Field label="Slug (URL)" hint="Optional — auto-generated from name">
              <input
                className="input-brut"
                data-testid={ADMIN.intakeSlug}
                placeholder="kao-enterprise-llc"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              />
            </Field>
            <Field label="Description">
              <input
                className="input-brut"
                data-testid={ADMIN.intakeDescription}
                placeholder="FTTH crews · project sites"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Field>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <LogoFrame
              src={form.logo_data_url}
              alt="Client logo"
              size={52}
              fallback={<ClientPlaceholderMark text={form.client_name} size={52} />}
            />
            <label className="btn-brut cursor-pointer">
              <Upload size={14} /> Upload client logo
              <input
                type="file"
                accept="image/*,.svg"
                className="hidden"
                data-testid="admin-intake-logo-input"
                onChange={async (e) => {
                  const url = await pickLogo(e);
                  if (url != null) setForm((f) => ({ ...f, logo_data_url: url }));
                }}
              />
            </label>
            {form.logo_data_url && (
              <button
                className="btn-brut btn-ghost text-xs"
                onClick={() => setForm((f) => ({ ...f, logo_data_url: null }))}
              >
                <Trash2 size={12} /> Remove
              </button>
            )}
            <div className="text-xs text-black/60 font-mono ml-auto">
              PNG / JPG / SVG · max 300KB
            </div>
          </div>

          <div className="mt-4">
            <button
              className="btn-brut btn-flame"
              data-testid={ADMIN.intakeCreate}
              onClick={create}
              disabled={creating}
            >
              {creating ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              Create intake
            </button>
          </div>
        </div>

        {loading ? (
          <div className="font-mono text-sm text-black/60">Loading intakes…</div>
        ) : items.length === 0 ? (
          <div className="brut-card p-8 text-center">
            <div className="font-mono text-xs uppercase tracking-widest text-black/60 mb-1">
              No intakes yet
            </div>
            <div>Create your first client intake above.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((it) => {
              const link = `${FRONTEND_ORIGIN}/apply/${it.slug}`;
              return (
                <div
                  key={it.id}
                  data-testid={ADMIN.intakeRow(it.id)}
                  className="brut-card p-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <LogoFrame
                        src={it.logo_data_url}
                        alt={`${it.client_name} logo`}
                        size={52}
                        fallback={<ClientPlaceholderMark text={it.client_name} size={52} />}
                      />
                      <div className="min-w-0">
                        <div className="font-display text-2xl">{it.client_name}</div>
                        {it.description && (
                          <div className="text-sm text-black/70 mt-1">{it.description}</div>
                        )}
                      </div>
                    </div>
                    <StatusPill status={it.active ? "verified" : "on_hold"} />
                  </div>

                  <div className="dashed-line my-4" />

                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55 mb-1">
                    Shareable link
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-xs bg-[rgb(var(--paper-2))] border-2 border-black px-2 py-1 flex-1 min-w-0 truncate">
                      {link}
                    </code>
                    <button
                      className="btn-brut btn-ghost text-xs"
                      data-testid={ADMIN.intakeCopyLink(it.id)}
                      onClick={() => copyLink(it.slug)}
                    >
                      <Copy size={12} /> Copy
                    </button>
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="font-mono text-xs text-black/70">
                      {it.candidate_count} candidate{it.candidate_count === 1 ? "" : "s"}
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="btn-brut btn-ghost text-xs"
                        data-testid={`admin-intake-edit-${it.id}`}
                        onClick={() => setEditing({ ...it })}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        className="btn-brut btn-ghost text-xs"
                        data-testid={ADMIN.intakeToggle(it.id)}
                        onClick={() => toggleActive(it)}
                      >
                        {it.active ? (
                          <>
                            <PowerOff size={12} /> Deactivate
                          </>
                        ) : (
                          <>
                            <Power size={12} /> Activate
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Edit modal */}
      {editing && (
        <div
          className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4"
          onClick={() => setEditing(null)}
        >
          <div
            className="brut-card bg-[rgb(var(--paper))] max-w-lg w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-xs uppercase tracking-widest">
                Edit intake · {editing.client_name}
              </div>
              <button
                className="btn-brut btn-ghost text-xs"
                onClick={() => setEditing(null)}
              >
                <X size={12} />
              </button>
            </div>
            <div className="space-y-4">
              <Field label="Client name" required>
                <input
                  className="input-brut"
                  value={editing.client_name}
                  onChange={(e) => setEditing((s) => ({ ...s, client_name: e.target.value }))}
                />
              </Field>
              <Field label="Description">
                <input
                  className="input-brut"
                  value={editing.description || ""}
                  onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                />
              </Field>
              <Field label="Logo">
                <div className="flex items-center gap-3 flex-wrap">
                  <LogoFrame
                    src={editing.logo_data_url}
                    alt="Logo"
                    size={52}
                    fallback={<ClientPlaceholderMark text={editing.client_name} size={52} />}
                  />
                  <label className="btn-brut cursor-pointer">
                    <Upload size={14} /> Replace logo
                    <input
                      type="file"
                      accept="image/*,.svg"
                      className="hidden"
                      onChange={async (e) => {
                        const url = await pickLogo(e);
                        if (url != null) setEditing((s) => ({ ...s, logo_data_url: url }));
                      }}
                    />
                  </label>
                  {editing.logo_data_url && (
                    <button
                      className="btn-brut btn-ghost text-xs"
                      onClick={() => setEditing((s) => ({ ...s, logo_data_url: "" }))}
                    >
                      <Trash2 size={12} /> Remove
                    </button>
                  )}
                </div>
              </Field>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button className="btn-brut btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn-brut btn-flame" onClick={saveEdit}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
